import { Controller } from "@hotwired/stimulus"

// Dégradé de bord bas sur une zone qui défile : la liste est coupée net par `max-height`, rien
// ne disait qu'il en restait. Le voile n'apparaît que tant qu'il reste à lire — arrivé au bout,
// il salirait une liste qu'on a fini de parcourir (même parti pris que le voile latéral des
// tableaux Stripe).
//
// Le voile vit sur l'élément du contrôleur, pas sur le conteneur qui défile : posé sur ce
// dernier, il défilerait avec le contenu au lieu de rester collé au bord.
export default class extends Controller {
  static targets = ["scroller"]

  // `axis: "horizontal"` pour une piste qui défile de côté (les onglets Match(s)) :
  // même voile, posé sur le bord droit par le CSS du composant.
  static values = { axis: { type: String, default: "vertical" } }

  // Sous ce reste à défiler, on est au bout à un arrondi près (zoom, défilement fractionnaire).
  static END_THRESHOLD = 2

  // Dans `initialize` et non `connect` : Stimulus annonce les cibles DÉJÀ présentes avant
  // d'appeler `connect`, et un écouteur posé avec un `this.update` encore indéfini ne se
  // déclenche jamais — le voile restait alors figé, même arrivé en bas de liste.
  initialize() {
    this.update = () => this.refresh()
    this.scheduleUpdate = () => this.scheduleRefresh()
  }

  connect() {
    this.resizeObserver = new ResizeObserver(this.update)
    this.resizeObserver.observe(this.element)

    // Un ResizeObserver ne voit que la boîte de l'élément : la HAUTEUR DU CONTENU d'un
    // conteneur qui défile lui échappe. Or elle bouge deux fois après notre connexion — quand
    // Font Awesome remplace chaque <i> par un <svg>, et quand la page suivante du scroll
    // infini arrive. Sans ce second observateur, le voile gardait l'état calculé avant, et
    // ne se corrigeait qu'au premier coup de molette.
    this.mutationObserver = new MutationObserver(this.scheduleUpdate)

    // Rattrape la cible annoncée avant nous : réattacher le même écouteur ne fait rien.
    if (this.hasScrollerTarget) this.scrollerTargetConnected(this.scrollerTarget)
    this.refresh()
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    this.mutationObserver?.disconnect()
    this.mutationObserver = null
    clearTimeout(this.refreshTimer)
    this.refreshTimer = null
  }

  // Le contenu de la carte est remplacé par Turbo à chaque filtre : la cible va et vient.
  scrollerTargetConnected(scroller) {
    scroller.addEventListener("scroll", this.update, { passive: true })
    this.resizeObserver?.observe(scroller)
    this.mutationObserver?.observe(scroller, { childList: true, subtree: true })
    this.refresh()
  }

  scrollerTargetDisconnected(scroller) {
    scroller.removeEventListener("scroll", this.update)
  }

  // Une substitution Font Awesome, c'est une mutation par icône : on en attend la fin plutôt
  // que de recalculer quarante fois. `setTimeout` et non `requestAnimationFrame`, que le
  // navigateur suspend dans un onglet en arrière-plan.
  scheduleRefresh() {
    if (this.refreshTimer) return

    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null
      this.refresh()
    }, 0)
  }

  refresh() {
    if (!this.hasScrollerTarget) return

    const scroller = this.scrollerTarget
    const horizontal = this.axisValue === "horizontal"
    const remaining = horizontal
      ? scroller.scrollWidth - scroller.clientWidth - scroller.scrollLeft
      : scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop
    const scrolled = horizontal ? scroller.scrollLeft : scroller.scrollTop

    this.element.dataset.scrollFadeEnd = String(remaining > this.constructor.END_THRESHOLD)
    // Une piste horizontale peut être coupée des deux côtés : l'onglet ouvert
    // est amené dans la vue au chargement, et les premiers sortent à gauche.
    this.element.dataset.scrollFadeStart = String(scrolled > this.constructor.END_THRESHOLD)
  }
}
